import * as React from 'react';
import cx from 'classnames';

import { useController } from './individuals-table.controller';
import { IndividualsTableProps } from './individuals-table.types';
import { NewTable, NewTableSelection } from '@common/components/new-table';
import { MODULE_NAME } from '@modules/pedigrees/constants/shared';
import { I18n } from 'react-redux-i18n';
import { ProfileSelector } from '@common/components/profile-selector';
import { IndividualRenaming } from './components/individual-renaming';
import { NewQuestionnaire } from '@common/components/new-questionnaire';
import { QuestionnaireOpen } from '@common/components/questionnaire-open';
import { NewInvite } from '@common/components/new-invite';
import { QuestionnaireListDto } from '@common/dto/questionnaire/list-dto';

import './individuals-table.styles.scss'

export const IndividualsTable: React.FC<IndividualsTableProps> = (props) => {
  const { 
    saveIndividualEmail,
    filters,
    itemUniqProperty,
    getSentQuestionnaires,
    selectedItems,
    data,
    totalItemsCount,
    loadMetadata,
    className,
    loading,
    getIndividualEmail,
    showInfoMessage,
    getFoldersForAddingColumns,
    onSelectedItemsChange,
    showErrorMessage,
    getFamilyIdMembersCount,
    getIndividualFamilyData,
    getPedigreeFolders,
    createPedigreeAndMoveToFolder,
    checkPedigreeNameExists,
    getQuestionnaires,
  } = props;
  const {
    newInviteProps,
    closeNewInvite,
    handleNewInviteSend,
    closeOpenQuestionnaire,
    handleOpenQuestionnaire,
    openQuestionnaireProps,
    handleGetQuestionnairesForNewQuestionnaire,
    handleNewQuestionnaire,
    newQuestionnaireProps,
    closeNewQuestionnaire,
    handleIndividualRename,
    individualRenamingProps,
    closeIndividualRename,
    changingColumns,
    handleColumnAdd,
    handleColumnRemove,
    handleGetFields,
    getHeaderElementId,
    getHeaderCellElementId,
    getBodyRowElementId,
    getBodyCellElementId,
    getProfiles,
    handleProfileSelectorSubmit,
    closeProfileSelector,
    profileSelectorOpened,
    columnActions,
    headerActions,
    tableColumns,
    getData,
    handleColumnsResizing,
    handleColumnsOrderChange,
    hasEmail,
    questionnaire
  } = useController(props);
  
  
  return (
    <>
      {newInviteProps && (
        <NewInvite
          getFamilyIdMembersCount={getFamilyIdMembersCount}
          getIndividualFamilyData={getIndividualFamilyData}
          getPedigreeFolders={getPedigreeFolders}
          createPedigreeAndMoveToFolder={createPedigreeAndMoveToFolder}
          checkPedigreeNameExists={checkPedigreeNameExists}
          getQuestionnaires={getQuestionnaires}
          individualId={newInviteProps.individualId}
          showErrorMessage={showErrorMessage}
          showInfoMessage={showInfoMessage}
          getIndividualEmail={(questionnaire: QuestionnaireListDto) => {
            return getIndividualEmail({
              fhq4: questionnaire.fhq4,
              individualId: newInviteProps.individualId,
              questionnaireId: questionnaire.questionnaireId,
            });
          }}
          onClose={closeNewInvite}
          onSubmit={handleNewInviteSend}
          saveIndividualEmail={saveIndividualEmail}
        />
      )}
      {newQuestionnaireProps && (
        <NewQuestionnaire
          moduleName={MODULE_NAME}
          title={`${I18n.t("New Questionnaire for")} ${newQuestionnaireProps?.individualName}`}
          showErrorMessage={showErrorMessage}
          onClose={closeNewQuestionnaire}
          getQuestionnaires={handleGetQuestionnairesForNewQuestionnaire}
          onSubmit={handleNewQuestionnaire}
          hasEmail={hasEmail}
          newQuestionnaire={questionnaire}
          individualId={newQuestionnaireProps?.individualId}
        />
      )}
      {openQuestionnaireProps && (
        <QuestionnaireOpen
          itemName={openQuestionnaireProps.individualName}
          showErrorMessage={showErrorMessage}
          onClose={closeOpenQuestionnaire}
          onSubmit={handleOpenQuestionnaire}
          getQuestionnaires={() => getSentQuestionnaires(openQuestionnaireProps.individualId)}
        />
      )}
      {individualRenamingProps && (
        <IndividualRenaming
          onClose={closeIndividualRename} 
          onSubmit={handleIndividualRename} 
          showErrorMessage={showErrorMessage}
          defaultValue={individualRenamingProps?.name}
        />
      )}
      {profileSelectorOpened && (
        <ProfileSelector
          onClose={closeProfileSelector}
          onSubmit={handleProfileSelectorSubmit}
          showErrorMessage={showErrorMessage}
          getData={getProfiles}
        />
      )}
      <NewTable 
        infiniteScroll
        disableAutoInit
        classes={{
          root: cx('PedigreesIndividualsTable', className)
        }}
        columnsResizing={{
          onChange: handleColumnsResizing
        }}
        columnsOrder={{
          onChange: handleColumnsOrderChange
        }}
        columnActions={columnActions}
        headerActions={headerActions}
        getId={{
          header: getHeaderElementId,
          headerCell: getHeaderCellElementId,
          bodyRow: getBodyRowElementId,
          bodyCell: getBodyCellElementId,
        }}
        selection={{
          type: 'multiple',
          items: selectedItems,
          onChange: onSelectedItemsChange as NewTableSelection['onChange']
        }}
        columnsManagement={{
          onAdd: handleColumnAdd,
          onRemove: handleColumnRemove,
          getFolders: getFoldersForAddingColumns,
          getFields: handleGetFields,
        }}
        filters={filters}
        loading={{ list: !loadMetadata || loading, columns: changingColumns, }}
        moduleName={`${MODULE_NAME}_individual_table`}
        noDataMessage={I18n.t('There no individuals to display')}
        errorMessage={{ list: I18n.t('Failed to load individuals') }}
        itemUniqProperty={itemUniqProperty}
        data={data}
        columns={tableColumns}
        totalItemsCount={totalItemsCount}
        loadMetadata={loadMetadata}
        onLoadData={getData}
      />
    </>
  );
};